import { ReactNode, useRef, useState } from "react";
import { Button, ButtonGroup, Form, Overlay, Popover } from "react-bootstrap";
import {
	faCheck,
	faFilter,
	faMultiply,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// ─── Three-way toggle (All / ✓ / ✗) ─────────────────────────────────
export type BoolToggleState = "all" | "true" | "false";
export const ThreeWayToggle = ({
	label,
	state,
	onChange,
}: {
	label: string;
	state: BoolToggleState;
	onChange: (state: BoolToggleState) => void;
}) => (
	<div className="d-flex align-items-center justify-content-between">
		<Form.Label className="fw-bold mb-0">{label}</Form.Label>
		<div style={{ flex: 1, maxWidth: "var(--filter-input-width, 70%)" }}>
			<ButtonGroup size="sm">
				<Button
					variant={state === "all" ? "primary" : "outline-secondary"}
					onClick={() => onChange("all")}
				>
					All
				</Button>
				<Button
					variant={state === "true" ? "success" : "outline-secondary"}
					onClick={() => onChange("true")}
					aria-label="True"
				>
					<FontAwesomeIcon icon={faCheck} />
				</Button>
				<Button
					variant={state === "false" ? "danger" : "outline-secondary"}
					onClick={() => onChange("false")}
					aria-label="False"
				>
					<FontAwesomeIcon icon={faMultiply} />
				</Button>
			</ButtonGroup>
		</div>
	</div>
);
// ─── Filter popover shell ────────────────────────────────────────────
interface FilterPopoverProps {
	children: ReactNode;
	popoverId: string;
	minWidth?: string;
	maxWidth?: string;
	inputWidth?: string;
	applyDisabled?: boolean;
	onApply: () => void;
	onClear: () => void;
}
export const useFilterPopover = () => {
	const [show, setShow] = useState(false);
	const [target, setTarget] = useState(null);
	const [hasActiveFilter, setHasActiveFilter] = useState(false);
	const ref = useRef(null);
	const handleIconClick = (event) => {
		setShow(!show);
		setTarget(event.target);
	};
	const close = () => setShow(false);
	return {
		show,
		target,
		hasActiveFilter,
		setHasActiveFilter,
		ref,
		handleIconClick,
		close,
	};
};
export const FilterPopover = ({
	children,
	popoverId,
	minWidth = "380px",
	maxWidth = "440px",
	inputWidth = "70%",
	applyDisabled = false,
	onApply,
	onClear,
	popoverState,
}: FilterPopoverProps & {
	popoverState: ReturnType<typeof useFilterPopover>;
}) => {
	const { show, target, hasActiveFilter, ref, handleIconClick, close } =
		popoverState;
	return (
		<div ref={ref}>
			<FontAwesomeIcon
				onClick={handleIconClick}
				className={hasActiveFilter ? "text-primary" : ""}
				icon={faFilter}
				style={{ cursor: "pointer", fontSize: "1.2rem" }}
			/>
			<Overlay
				show={show}
				target={target}
				placement="bottom-start"
				container={ref}
				rootClose
				onHide={close}
			>
				<Popover id={popoverId} style={{ minWidth, maxWidth }}>
					<Popover.Header className="d-flex justify-content-end">
						<Button
							variant="link"
							className="text-dark p-0 text-decoration-none"
							onClick={close}
							style={{
								fontSize: "1.25rem",
								lineHeight: "1",
							}}
						>
							&times;
						</Button>
					</Popover.Header>
					<Popover.Body>
						<div
							className="d-flex flex-column gap-2"
							style={
								{
									"--filter-input-width": inputWidth,
								} as React.CSSProperties
							}
						>
							{children}
							<div className="d-flex gap-3 mt-2">
								<Button
									size="sm"
									variant="primary"
									onClick={onApply}
									disabled={applyDisabled}
									style={{ flex: 2 }}
								>
									Apply
								</Button>
								<Button
									size="sm"
									variant="outline-secondary"
									onClick={onClear}
									style={{ flex: 1 }}
								>
									Clear
								</Button>
							</div>
						</div>
					</Popover.Body>
				</Popover>
			</Overlay>
		</div>
	);
};
